import { createContext, useContext, useState, useEffect } from "react";
import type {ReactNode} from "react";
import { darkTheme, lightTheme } from "../styles/theme";

type Theme = typeof darkTheme;

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);
const STORAGE_KEY = "owner-theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "dark"; } catch { return false; }
  });
  const theme = isDark ? darkTheme : lightTheme;

  const toggleTheme = () => setIsDark(prev => {
    const next = !prev;
    try { localStorage.setItem(STORAGE_KEY, next ? "dark" : "light"); } catch {}
    return next;
  });

  useEffect(() => {
    document.documentElement.style.background = isDark ? "#07101f" : "#f0f4ff";
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
};